// src/pages/orders.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import api from '../store/api/base';
import Loader from '../components/common/loader';
import { setError } from '../store/slices/uiSlice';
import OrderStatusBadge from '../components/orders/orderStatusBadge';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// ─── Shipment status pill ─────────────────────────────────────────────────────
// Uses lastUpdate from order timeline + estimatedDelivery from Shiprocket
const ShipmentPill = ({ order }) => {
  const { status, estimatedDelivery, lastUpdate } = order;

  if (status === 'DELIVERED') {
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
        ✅ Delivered
      </span>
    );
  }
  if (status === 'CANCELLED') {
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">
        🚫 Cancelled
      </span>
    );
  }
  if (status === 'RETURNED') {
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-medium">
        ↩️ Returned
      </span>
    );
  }

  if (status === 'SHIPPED') {
    const courierStatusMap = {
      PICKUP_SCHEDULED:        { label: 'Pickup Scheduled',   color: 'bg-blue-100 text-blue-700',    icon: '📅' },
      PICKED_UP:               { label: 'Picked Up',          color: 'bg-indigo-100 text-indigo-700', icon: '📦' },
      IN_TRANSIT:              { label: 'In Transit',          color: 'bg-purple-100 text-purple-700', icon: '🚚' },
      REACHED_DESTINATION_HUB: { label: 'At Destination Hub', color: 'bg-purple-100 text-purple-700', icon: '🏭' },
      OUT_FOR_DELIVERY:        { label: 'Out for Delivery',   color: 'bg-blue-100 text-blue-800',    icon: '🏃' },
      ATTEMPTED_DELIVERY:      { label: 'Delivery Attempted', color: 'bg-yellow-100 text-yellow-700', icon: '🔔' },
      UNDELIVERED:             { label: 'Delivery Attempted', color: 'bg-yellow-100 text-yellow-700', icon: '🔔' },
      DELAYED:                 { label: 'Delayed',            color: 'bg-yellow-100 text-yellow-700', icon: '⏳' },
    };
    const mapped = courierStatusMap[lastUpdate?.status];
    if (mapped) {
      return (
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${mapped.color}`}>
          {mapped.icon} {mapped.label}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
        🚚 Shipped
      </span>
    );
  }

  if (status === 'PROCESSING') {
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
        ⚙️ Processing
      </span>
    );
  }
  if (status === 'CONFIRMED') {
    return (
      <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
        ✔️ Confirmed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
      🕐 Pending
    </span>
  );
};

// ─── ETA cell ─────────────────────────────────────────────────────────────────
const ETACell = ({ order }) => {
  const { estimatedDelivery, status } = order;
  if (['DELIVERED', 'CANCELLED', 'RETURNED'].includes(status) || !estimatedDelivery) {
    return <span className="text-gray-400 text-xs">—</span>;
  }
  const isOverdue = new Date(estimatedDelivery) < new Date();
  return (
    <div>
      <p className={`text-xs font-medium ${isOverdue ? 'text-red-600' : 'text-blue-700'}`}>
        {isOverdue ? '⚠️ ' : ''}{formatDate(estimatedDelivery)}
      </p>
      <p className="text-xs text-gray-400">via Shiprocket</p>
    </div>
  );
};

// ─── Filter tabs ──────────────────────────────────────────────────────────────
const STATUS_FILTERS = [
  { label: 'All',        value: ''           },
  { label: 'Confirmed',  value: 'CONFIRMED'  },
  { label: 'Processing', value: 'PROCESSING' },
  { label: 'Shipped',    value: 'SHIPPED'    },
  { label: 'Delivered',  value: 'DELIVERED'  },
  { label: 'Cancelled',  value: 'CANCELLED'  },
];

// ─── Main ─────────────────────────────────────────────────────────────────────
const Orders = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const [loading, setLoading]           = useState(true);
  const [orders, setOrders]             = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination]     = useState({ total: 0, page: 1, pages: 1, limit: 10 });

  const fetchOrders = useCallback(async (page = 1, status = '') => {
    try {
      setLoading(true);
      const params = { page, limit: 10 };
      if (status) params.status = status;
      if (status === 'CANCELLED') params.includeAll = true;
      const response = await api.get('/orders', { params });
      setOrders(response.data.data.orders);
      setPagination(response.data.data.pagination);
    } catch (error) {
      dispatch(setError('Failed to load your orders'));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => { fetchOrders(1, statusFilter); }, [statusFilter, fetchOrders]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchOrders(newPage, statusFilter);
  };

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (loading && orders.length === 0) {
    return <div className="container mx-auto px-4 py-8 flex justify-center"><Loader size="lg" /></div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-2xl font-bold">My Orders</h1>
        {pagination.total > 0 && (
          <p className="text-sm text-gray-500">{pagination.total} order{pagination.total !== 1 ? 's' : ''}</p>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => handleFilterChange(f.value)}
            className={`px-3 py-1.5 text-sm rounded-full border font-medium transition-colors ${
              statusFilter === f.value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Empty */}
      {!loading && orders.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold mb-1 text-gray-800">
            {statusFilter ? `No ${statusFilter.toLowerCase()} orders` : 'No Orders Yet'}
          </h2>
          <p className="text-gray-500 text-sm mb-5">
            {statusFilter ? 'Try a different filter.' : "You haven't placed any orders yet."}
          </p>
          {!statusFilter && (
            <Link to="/products" className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
              Browse Products
            </Link>
          )}
        </div>
      )}

      {orders.length > 0 && (
        <>
          {/* Loading overlay */}
          {loading && <div className="flex justify-center py-3"><Loader size="sm" /></div>}

          {/* ── Desktop table ── */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Shipment</th>
                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ETA</th>
                    <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map(order => (
                    <tr
                      key={order.orderId}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/order/${order.orderId}`)}
                    >
                      {/* Order ID + items preview */}
                      <td className="py-4 px-4">
                        <p className="font-semibold text-blue-600 text-sm">#{order.orderId}</p>
                        <div className="flex items-center mt-1">
                          {order.items?.slice(0, 2).map((item, idx) => (
                            <div
                              key={idx}
                              className="w-7 h-7 rounded-full overflow-hidden bg-gray-100 border flex-shrink-0"
                              style={{ marginLeft: idx > 0 ? '-6px' : '0' }}
                            >
                              <img src={item.imageUrl || '/images/placeholder.png'} alt={item.name} className="w-full h-full object-cover" />
                            </div>
                          ))}
                          {order.itemCount > 2 && (
                            <span className="text-xs text-gray-400 ml-1.5">+{order.itemCount - 2}</span>
                          )}
                        </div>
                      </td>

                      <td className="py-4 px-4 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(order.createdAt)}
                      </td>

                      <td className="py-4 px-4 whitespace-nowrap">
                        <OrderStatusBadge status={order.status} />
                      </td>

                      {/* Shiprocket shipment status */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        <ShipmentPill order={order} />
                      </td>

                      {/* Shiprocket ETA */}
                      <td className="py-4 px-4 whitespace-nowrap">
                        <ETACell order={order} />
                      </td>

                      <td className="py-4 px-4 text-right whitespace-nowrap">
                        <span className="font-semibold text-sm">
                          ₹{(order.totalAmount || 0).toLocaleString('en-IN')}
                        </span>
                      </td>

                      <td
                        className="py-4 px-4 text-right whitespace-nowrap"
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/order/${order.orderId}`}
                            className="text-blue-600 hover:underline text-xs font-medium"
                          >
                            View
                          </Link>
                          {order.isInvoiceAvailable && (
                            <>
                              <span className="text-gray-300">|</span>
                              <Link
                                to={`/order/${order.orderId}`}
                                className="text-green-600 hover:underline text-xs font-medium"
                              >
                                Invoice
                              </Link>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Mobile card view ── */}
          <div className="md:hidden space-y-3">
            {orders.map(order => (
              <div
                key={order.orderId}
                className="bg-white rounded-lg shadow-sm border p-4 cursor-pointer active:bg-gray-50"
                onClick={() => navigate(`/order/${order.orderId}`)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-semibold text-blue-600 text-sm">#{order.orderId}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(order.createdAt)}</p>
                  </div>
                  <OrderStatusBadge status={order.status} />
                </div>

                {/* Items preview */}
                <div className="flex items-center gap-1.5 mb-3">
                  {order.items?.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="w-10 h-10 rounded overflow-hidden bg-gray-100 border flex-shrink-0">
                      <img src={item.imageUrl || '/images/placeholder.png'} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {order.itemCount > 3 && (
                    <span className="text-xs text-gray-400">+{order.itemCount - 3}</span>
                  )}
                </div>

                {/* Shipment + ETA */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <ShipmentPill order={order} />
                  {order.estimatedDelivery && !['DELIVERED', 'CANCELLED', 'RETURNED'].includes(order.status) && (
                    <span className="text-xs text-blue-600">ETA: {formatDate(order.estimatedDelivery)}</span>
                  )}
                </div>

                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-bold text-gray-800 text-sm">
                    ₹{(order.totalAmount || 0).toLocaleString('en-IN')}
                  </span>
                  <span className="text-blue-600 text-sm font-medium">View →</span>
                </div>
              </div>
            ))}
          </div>

          {/* ── Pagination ── */}
          {pagination.pages > 1 && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 bg-white rounded-lg border px-4 py-3 shadow-sm">
              <p className="text-sm text-gray-600">
                Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                {' '}–{' '}
                <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                {' '}of <span className="font-medium">{pagination.total}</span> orders
              </p>

              <nav className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1.5 text-sm rounded border disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Prev
                </button>

                {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === pagination.pages || Math.abs(p - pagination.page) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === '...' ? (
                      <span key={`e-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        className={`px-3 py-1.5 text-sm rounded border transition-colors ${
                          pagination.page === p ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1.5 text-sm rounded border disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next →
                </button>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Orders;